/**
 * EveryINC - User Helpers Module
 * User-related utility functions
 */

import type { User, Incident, RoleGroup, DeveloperPerformance, UserRole } from './types';

function getUserFullName(user: User | null | undefined): string {
  if (!user) return '';

  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }

  return String(user.name || '').trim();
}

function buildUserInitials(name: string | null | undefined): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '--';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function normalizeComparableText(value: string | null | undefined): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function formatDurationCompact(durationMs: number | null | undefined): string {
  if (!durationMs || Number.isNaN(durationMs)) {
    return 'Sem base ainda';
  }

  const totalMinutes = Math.round(durationMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes} min em media`;

  const totalHours = Math.round(durationMs / 3600000);
  if (totalHours < 24) return `${totalHours} h em media`;

  const totalDays = (durationMs / 86400000).toFixed(1).replace('.0', '');
  return `${totalDays} d em media`;
}

interface RoleGroupConfig {
  role: UserRole;
  label: string;
  accent: string;
}

function buildUserRoleGroups(users: User[]): RoleGroup[] {
  const groups: RoleGroupConfig[] = [
    { role: 'admin', label: 'Administradores', accent: 'admin' },
    { role: 'owner', label: 'Owners', accent: 'owner' },
    { role: 'developer', label: 'Desenvolvedores', accent: 'developer' },
    { role: 'user', label: 'Usuarios', accent: 'user' }
  ];

  return groups.map(group => {
    const members = users.filter(user => user.role === group.role);
    return { ...group, members, count: members.length };
  });
}

function buildRoleCompositionStyle(roleGroups: RoleGroup[], totalUsers: number): string {
  if (!totalUsers) {
    return 'background: conic-gradient(var(--chart-track) 0deg 360deg);';
  }

  const colors: Record<string, string> = {
    admin: 'var(--role-admin)',
    owner: 'var(--role-owner)',
    developer: 'var(--role-developer)',
    user: 'var(--role-user)'
  };

  let start = 0;
  const stops = roleGroups.map(group => {
    const angle = (group.count / totalUsers) * 360;
    const end = start + angle;
    const color = colors[group.accent] || 'var(--status-fechado-border)';
    const stop = `${color} ${start}deg ${end}deg`;
    start = end;
    return stop;
  });

  if (start < 360) {
    stops.push(`var(--chart-track) ${start}deg 360deg`);
  }

  return `background: conic-gradient(${stops.join(', ')});`;
}

function buildDeveloperPerformance(users: User[], incidents: Incident[]): DeveloperPerformance[] {
  const developers = users.filter(user => user.role === 'developer');

  return developers.map(user => {
    const userName = getUserFullName(user);
    const assignedIncidents = incidents.filter(incident =>
      normalizeComparableText(incident.assigned_dev) === normalizeComparableText(userName)
    );
    const resolvedIncidents = assignedIncidents.filter(incident =>
      ['resolvido', 'fechado'].includes(incident.status)
    );
    const totalResolutionMs = resolvedIncidents.reduce((sum, incident) => {
      const resolvedAt = incident.resolved_at || incident.closed_at;
      if (!resolvedAt || !incident.created_at) return sum;

      const duration = new Date(resolvedAt).getTime() - new Date(incident.created_at).getTime();
      return duration > 0 ? sum + duration : sum;
    }, 0);

    return {
      id: user.id,
      name: userName,
      resolved: resolvedIncidents.length,
      inProgress: assignedIncidents.filter(incident =>
        ['aberto', 'em_andamento'].includes(incident.status)
      ).length,
      averageResolutionMs: resolvedIncidents.length > 0 ? totalResolutionMs / resolvedIncidents.length : null
    };
  }).sort((a, b) => {
    if (b.resolved !== a.resolved) return b.resolved - a.resolved;
    if (a.averageResolutionMs === null) return 1;
    if (b.averageResolutionMs === null) return -1;
    return a.averageResolutionMs - b.averageResolutionMs;
  }).slice(0, 6);
}

const EveryIncUserHelpers = {
  getUserFullName,
  buildUserInitials,
  normalizeComparableText,
  formatDurationCompact,
  buildUserRoleGroups,
  buildRoleCompositionStyle,
  buildDeveloperPerformance
};

// Attach to window for IIFE compatibility
(window as Window).EveryIncUserHelpers = EveryIncUserHelpers;

export {
  getUserFullName,
  buildUserInitials,
  normalizeComparableText,
  formatDurationCompact,
  buildUserRoleGroups,
  buildRoleCompositionStyle,
  buildDeveloperPerformance
};

export default EveryIncUserHelpers;
